import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, getSecondaryAuth, releaseSecondaryApp } from '../firebase';
import { ROLES } from '../utils/constants';

const ACCOUNT_ERROR_MESSAGES = {
  'auth/email-already-in-use': 'Cet email est déjà utilisé',
  'auth/invalid-email': "Format d'email invalide",
  'auth/weak-password': 'Le mot de passe est trop faible (6 caractères minimum)'
};

export class AuthService {

  // Créer compte
  static async createAccount(email, password, userData) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: userData.username
      });

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: email,
        username: userData.username,
        bureau: userData.bureau || '',
        telephone: userData.telephone || '',
        adresse: userData.adresse || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date(),
        isActive: true,
        role: 'architect',
        preferences: {
          zoneMerchichParDefaut: 'EPSG:26191',
          notifications: true,
          theme: 'light'
        }
      });

      return { success: true, user };
    } catch (error) {
      console.error('Erreur création compte:', error);
      let errorMessage = 'Erreur lors de la création du compte';

      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Cet email est déjà utilisé';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        case 'auth/weak-password':
          errorMessage = 'Le mot de passe est trop faible';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }

  // Créer le compte d'un agent (membre du cabinet) sans déconnecter l'utilisateur courant.
  // Le compte Auth est créé via une app Firebase secondaire ; le document membre est
  // écrit par le propriétaire (session courante) pour respecter les règles Firestore.
  static async createMemberAccount(cabinetId, { email, password, agentName, role }) {
    const normalizedRole = Object.values(ROLES).includes(role) ? role : ROLES.TECHNICIEN;
    let secondaryAuth = null;

    try {
      secondaryAuth = getSecondaryAuth();
      const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const newUser = credential.user;

      try {
        await updateProfile(newUser, { displayName: agentName });
      } catch (e) {
        // non bloquant
      }

      await setDoc(doc(db, 'users', newUser.uid), {
        uid: newUser.uid,
        email,
        username: agentName,
        agentName,
        role: normalizedRole,
        cabinetId,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        preferences: {
          zoneMerchichParDefaut: 'EPSG:26191',
          notifications: true,
          theme: 'light'
        }
      });

      return { success: true, uid: newUser.uid };
    } catch (error) {
      console.error('Erreur création compte agent:', error);
      return {
        success: false,
        error: ACCOUNT_ERROR_MESSAGES[error.code] || error.message
      };
    } finally {
      if (secondaryAuth) {
        try {
          await signOut(secondaryAuth);
        } catch (e) {
          // ignore
        }
      }
      await releaseSecondaryApp();
    }
  }

  // Se connecter
  static async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let userData = null;

      if (userDoc.exists()) {
        userData = userDoc.data();

        if (userData.isActive === false) {
          await signOut(auth);
          return { success: false, error: 'Ce compte a été désactivé. Contactez votre cabinet.' };
        }

        await setDoc(doc(db, 'users', user.uid), {
          lastLoginAt: new Date(),
          updatedAt: new Date()
        }, { merge: true });
      }

      return {
        success: true,
        user: {
          ...user,
          userData
        }
      };
    } catch (error) {
      console.error('Erreur connexion:', error);
      let errorMessage = 'Erreur de connexion';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives. Réessayez plus tard';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Email ou mot de passe incorrect';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }

  // Se déconnecter
  static async signOut() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      return { success: false, error: error.message };
    }
  }

  // Réinitialiser mot de passe
  static async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Erreur réinit MDP:', error);
      let errorMessage = 'Erreur lors de la réinitialisation';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Format d\'email invalide';
          break;
        default:
          errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }

  // Changer son mot de passe (re-authentification requise par Firebase)
  static async changePassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        return { success: false, error: 'Utilisateur non connecté' };
      }
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Erreur changement MDP:', error);
      const messages = {
        'auth/wrong-password': 'Mot de passe actuel incorrect',
        'auth/invalid-credential': 'Mot de passe actuel incorrect',
        'auth/weak-password': 'Le nouveau mot de passe est trop faible (6 caractères minimum)',
        'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard'
      };
      return { success: false, error: messages[error.code] || error.message };
    }
  }

  // Écouter changements auth
  static onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          const userData = userDoc.exists() ? userDoc.data() : null;

          if (userData?.isActive === false) {
            await signOut(auth);
            callback(null);
            return;
          }

          callback({
            ...firebaseUser,
            userData
          });
        } catch (error) {
          console.error('Erreur récup données:', error);
          callback(firebaseUser);
        }
      } else {
        callback(null);
      }
    });
  }

  // Utilisateur actuel
  static getCurrentUser() {
    return auth.currentUser;
  }

  // Vérifier auth
  static isAuthenticated() {
    return !!auth.currentUser;
  }

  // MAJ profil
  static async updateUserProfile(userId, updates) {
    try {
      await setDoc(doc(db, 'users', userId), {
        ...updates,
        updatedAt: new Date()
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.error('Erreur MAJ profil:', error);
      return { success: false, error: error.message };
    }
  }

  // Récupérer profil utilisateur
  static async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      }
      return { success: false, error: 'Utilisateur non trouvé' };
    } catch (error) {
      console.error('Erreur récup profil:', error);
      return { success: false, error: error.message };
    }
  }
}
